import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, FileText, Activity, LogOut, Settings, CheckSquare, HelpCircle, Map, FolderOpen } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useApprovalQueue } from "../lib/approvals";
import { useRelease } from "../lib/release";
import { NotificationBell } from "./NotificationBell";
import { ClientViewBanner } from "./ClientView";
import { AdminNav } from "./AdminNav";
import EmployerLegalFooter from "./EmployerLegalFooter";
import MaintenanceNotice from "./MaintenanceNotice";
import { EmergencySessionBanner } from "./EmergencySessionBanner";
import { PrelaunchNotice, VersionStamp } from "./PrelaunchNotice";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const { count: approvalCount, isApprover } = useApprovalQueue();
  const { isOn } = useRelease();
  const invoiceService = user?.role !== "customer" || user?.invoice_service !== false;

  const items = isAdmin
    ? []
    : [
        { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, tid: "nav-dashboard" },
        ...(invoiceService ? [{ to: "/invoices", label: "Invoices", icon: FileText, tid: "nav-invoices" }] : []),
        ...(invoiceService && isOn("page.approvals") ? [{
          to: "/approvals",
          label: "Approvals",
          icon: CheckSquare,
          tid: "nav-approvals",
          badge: isApprover ? approvalCount : 0,
        }] : []),
        { flag: "page.prevue", to: "/analytics", label: "PreVue", icon: Activity, tid: "nav-analytics" },
        { to: "/files", label: "Files", icon: FolderOpen, tid: "nav-files" },
        { flag: "page.roadmap", to: "/roadmap", label: "Roadmap", icon: Map, tid: "nav-roadmap" },
        { flag: "page.settings", to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
        { flag: "page.help", to: "/help", label: "Help", icon: HelpCircle, tid: "nav-help" },
      ];

  const visible = items.filter((item) => !item.flag || isOn(item.flag));

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell">
      <ClientViewBanner />
      {isAdmin && <EmergencySessionBanner />}
      {isAdmin && <PrelaunchNotice />}
      {!isAdmin && <MaintenanceNotice />}
      <header
        className="sticky top-0 z-40 border-b"
        style={{ background: "rgba(247,247,246,0.75)", backdropFilter: "blur(16px)", borderColor: "var(--border)" }}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_err-dashboard-portal/artifacts/phsk29o5_E1-Primary-Logo_Small_Transparent.png"
              alt="EmployeesFirst"
              className="h-9 w-auto"
              data-testid="employeesfirst-header-logo"
            />
          </Link>

          <div className="flex items-center gap-3 shrink-0">
            {!isAdmin && <NotificationBell />}
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-semibold" style={{ color: "var(--ink)" }}>{user?.name}</div>
              <div className="eyebrow text-[9px]">{isAdmin ? "Employees First Portal" : user?.company}</div>
            </div>
            <button
              onClick={logout}
              data-testid="btn-logout"
              className="btn-ghost !py-2 !px-3 flex items-center gap-2 text-sm"
              aria-label="Sign out"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {isAdmin ? <AdminNav isOn={isOn} /> : (
        <nav className="border-t flex overflow-x-auto no-scrollbar" style={{ borderColor: "var(--border)" }} data-testid="secondary-nav">
          {visible.map((it) => {
            const Icon = it.icon;
            const active = loc.pathname === it.to || (it.to !== "/admin" && loc.pathname.startsWith(it.to));
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.tid}
                className="flex-1 min-w-[112px] px-3 py-3 text-xs font-medium flex items-center justify-center gap-1.5 whitespace-nowrap"
                style={{ color: active ? "var(--ink)" : "var(--muted)", borderBottom: active ? "2px solid var(--ink)" : "none" }}
              >
                <Icon size={13} /> {it.label}
                {Boolean(it.badge) && (
                  <span
                    className="inline-flex items-center justify-center text-[10px] font-bold min-w-[16px] h-[16px] px-1"
                    style={{ background: "var(--ink)", color: "var(--surface)" }}
                    data-testid="nav-approvals-badge"
                  >
                    {it.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {isAdmin ? (
        <footer className="border-t py-6 text-center eyebrow" style={{ borderColor: "var(--border)" }} data-testid="admin-shell-footer">
          EmployeesFirst · Secure client area
          <span className="block mt-1 text-[10px]" style={{ color: "var(--muted)" }}><VersionStamp /></span>
        </footer>
      ) : <EmployerLegalFooter />}
    </div>
  );
}
